import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { getGateAnalytics } from '@/data/gateAnalytics';
import { useState } from 'react';
import { TrendingUp, TrendingDown, Users, UserCheck, Clock, MousePointer } from 'lucide-react';

export function GateAnalytics() {
  const [days, setDays] = useState(7);
  
  const { data: stats, isLoading } = useQuery({
    queryKey: ['gate-analytics', days],
    queryFn: () => getGateAnalytics(days),
    staleTime: 1000 * 60 * 5, // 5 min
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Gate Conversion Analytics</CardTitle>
          <CardDescription>Loading analytics data...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!stats) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Gate Conversion Analytics</CardTitle>
            <CardDescription>Track product gate performance and user behavior</CardDescription>
          </div>
          <Select value={days.toString()} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="p-4 border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Total Impressions</p>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-3xl font-bold">{stats.impressions.toLocaleString()}</p>
          </div>

          <div className="p-4 border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Signups</p>
              <UserCheck className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-600">{stats.signups.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">
              +{stats.proClicks} pro clicks
            </p>
          </div>

          <div className="p-4 border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Conversion Rate</p>
              {stats.conversionRate > 15 ? (
                <TrendingUp className="h-4 w-4 text-green-600" />
              ) : (
                <TrendingDown className="h-4 w-4 text-orange-500" />
              )}
            </div>
            <p className={`text-3xl font-bold ${stats.conversionRate > 15 ? 'text-green-600' : 'text-orange-500'}`}>
              {stats.conversionRate}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.abandonmentRate}% abandoned
            </p>
          </div>

          <div className="p-4 border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Avg Time to Convert</p>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-3xl font-bold">{stats.avgTimeToConvert}s</p>
          </div>
        </div>

        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <MousePointer className="h-4 w-4" />
            Trigger Breakdown
          </h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Timer (6s)</span>
              <span className="font-medium">
                {stats.timerImpressions} ({stats.impressions > 0 ? Math.round((stats.timerImpressions / stats.impressions) * 100) : 0}%)
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Scroll (30%)</span>
              <span className="font-medium">
                {stats.scrollImpressions} ({stats.impressions > 0 ? Math.round((stats.scrollImpressions / stats.impressions) * 100) : 0}%)
              </span>
            </div>
          </div>
        </div>

        {stats.conversionRate < 10 && stats.impressions > 100 && (
          <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <p className="text-sm text-orange-800 dark:text-orange-200">
              <strong>⚠️ Low conversion rate detected.</strong> Consider adjusting gate copy or timing via system config.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
